import { useCallback, useMemo, useState } from 'react';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { MAX_RECIPIENT_OPTIONS } from '@dealstack/shared/lib/common/business/dealEvent';
import { useDebounceValue } from '@dealstack/frontend/shared/other/react/hooks/useDebounceValue';
import type { AsyncFilterMultiSelectProps } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { dealEventLogRequestOptions } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventLogUnread.ts';
import {
	withSelectedOption,
	type DealEventFilterOption,
} from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogFilterOptions.utils.ts';

interface Person {
	id?: string;
	name?: string;
	email: string;
}

interface Page {
	people: Person[];
	hasMore: boolean;
}

interface Params<TData> {
	dealId: string;
	enabled: boolean;
	selected?: string;
	queryOptions: (args: {
		headers: { 'deal-id': string };
		query: { limit: number; search?: string };
	}) => UseQueryOptions<TData>;
	page: (data: TData | undefined) => Page;
	toValue: (person: Person) => string;
}

export const useDealEventPeopleFilter = <TData>({
	dealId,
	enabled,
	selected,
	queryOptions,
	page,
	toValue,
}: Params<TData>) => {
	const [opened, setOpened] = useState(false);
	const [search, setSearch] = useState('');
	const debouncedSearch = useDebounceValue(search);

	const query = useQuery({
		...queryOptions({
			...dealEventLogRequestOptions(dealId),
			query: { limit: MAX_RECIPIENT_OPTIONS, search: debouncedSearch || undefined },
		}),
		enabled: enabled && opened && !!dealId,
		staleTime: 0,
	});

	const selectedQuery = useQuery({
		...queryOptions({
			...dealEventLogRequestOptions(dealId),
			query: { limit: 1, search: selected },
		}),
		enabled: enabled && !!dealId && !!selected,
		staleTime: Infinity,
	});

	const options = useMemo<DealEventFilterOption[]>(() => {
		const toOption = (person: Person) => ({
			value: toValue(person),
			label: person.name ?? person.email,
		});

		return withSelectedOption(
			page(query.data).people.map(toOption),
			page(selectedQuery.data)
				.people.map(toOption)
				.find(({ value }) => value === selected)
		);
	}, [query.data, selectedQuery.data, selected, page, toValue]);

	const onDropdownOpen = useCallback(() => setOpened(true), []);

	const async = useMemo<AsyncFilterMultiSelectProps>(
		() => ({
			serverSide: true,
			onDropdownOpen,
			onSearchChange: setSearch,
			isLoading: query.isFetching,
			hasMore: page(query.data).hasMore,
		}),
		[onDropdownOpen, query.isFetching, query.data, page]
	);

	return { options, async };
};
